import React, { useCallback, useState, useEffect } from 'react';
import { Box } from '@mui/material';

import { Container, Loading, ErrorMessage } from '@/src/ui';
import { apiRequest } from '@/src/services';
import type { SubmissionHistoryFilterState } from './models/filterDTOs';
import type { SubmissionHistoryRowDTO } from './models/submissionHistoryDTOs';
import { useAuth } from '@/src/app/context/useAuth';
import SubmissionHistoryFilters from './components/SubmissionHistoryFilters';
import SubmissionHistoryLegend from './components/SubmissionHistoryLegend';
import SubmissionHistoryTable from './components/SubmissionHistoryTable';

const SubmissionHistoryPage: React.FC = () => {
    document.title = 'Submission History';

    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');
    const [data, setData] = useState<SubmissionHistoryRowDTO[]>([]);
    const [hasSearched, setHasSearched] = useState(false);

    const { user } = useAuth();
    const userId = user?.id || 'nouser';
    const UNIVERSAL_FILTER_KEY = `universal-filters-${userId}`;

    const [filterState, setFilterState] = useState<SubmissionHistoryFilterState>(() => {
        try {
            const saved = localStorage.getItem(UNIVERSAL_FILTER_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                return {
                    districtId: parsed.districtId || 0,
                    schoolId: parsed.schoolId || 0,
                    gradeLevelMasterId: parsed.gradeLevelMasterId || 0,
                    classId: parsed.classId || 0,
                    examTypeMasterId: parsed.examTypeMasterId || 0,
                    examId:
                        parsed.examId ||
                        (parsed.examIds && parsed.examIds.length > 0 ? parsed.examIds[0] : 0),
                };
            }
        } catch {
            // intentionally empty
        }
        return {
            districtId: 0,
            schoolId: 0,
            gradeLevelMasterId: 0,
            classId: 0,
            examTypeMasterId: 0,
            examId: 0,
        };
    });

    useEffect(() => {
        if (!userId) return;
        try {
            const saved = localStorage.getItem(UNIVERSAL_FILTER_KEY);
            const existingFilters = saved ? JSON.parse(saved) : {};

            const universalFilters = {
                ...existingFilters,
                gradeLevelMasterId: filterState.gradeLevelMasterId,
                districtId: filterState.districtId,
                schoolId: filterState.schoolId,
                classId: filterState.classId,
                examTypeMasterId: filterState.examTypeMasterId,
                examId: filterState.examId,
                examIds: [filterState.examId],
            };
            localStorage.setItem(UNIVERSAL_FILTER_KEY, JSON.stringify(universalFilters));
        } catch {
            // intentionally empty
        }
    }, [filterState, userId, UNIVERSAL_FILTER_KEY]);

    const refreshTableData = useCallback(
        async ({
            districtId,
            schoolId,
            gradeLevelMasterId,
            classId,
            examTypeMasterId,
            examId,
        }: SubmissionHistoryFilterState = filterState) => {
            setError('');
            setLoaded(false);
            setHasSearched(true);

            if (!districtId || !schoolId || !gradeLevelMasterId) {
                setError('Please select District, School, and Grade.');
                setLoaded(true);
                return;
            }

            let requestUrl = `reports/submissionHistory/GetSubmissionHistory?districtId=${districtId}`;
            requestUrl += `&schoolId=${schoolId}`;
            requestUrl += `&gradeId=${gradeLevelMasterId}`;
            requestUrl += `&classId=${classId}`;
            requestUrl += `&examTypeId=${examTypeMasterId}`;
            requestUrl += `&examId=${examId}`;

            try {
                const res = await apiRequest<SubmissionHistoryRowDTO[]>('GET', requestUrl);
                setData(Array.isArray(res.data) ? res.data : []);
            } catch {
                setError('Failed to load Submission History Report');
            } finally {
                setLoaded(true);
            }
        },
        [filterState]
    );

    return (
        <Container>
            <h1 className="page-header">Submission History Report</h1>
            <br />

            <SubmissionHistoryFilters
                filterState={filterState}
                setFilterState={setFilterState}
                refreshTableData={refreshTableData}
            />

            <Box
                sx={{
                    pb: 2,
                }}
            >
                <SubmissionHistoryLegend dataLoaded={data !== null && data.length !== 0} />
            </Box>

            <Loading
                visible={hasSearched && !loaded}
                message="Loading Submission History Report..."
            />

            <ErrorMessage error={hasSearched ? error : ''} />

            {hasSearched && loaded && !error && <SubmissionHistoryTable rows={data} />}
        </Container>
    );
};

export default SubmissionHistoryPage;
